import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "react";
import type { FieldType } from "@/types/forms";

interface FieldTypePickerProps {
  onSelect: (type: FieldType) => void;
  disabled?: boolean;
}

const categories: { label: string; types: { type: FieldType; label: string }[] }[] = [
  {
    label: "Text",
    types: [
      { type: "short_text", label: "Short Text" },
      { type: "long_text", label: "Long Text" },
      { type: "rich_text", label: "Rich Text" },
    ],
  },
  {
    label: "Number",
    types: [
      { type: "number", label: "Number" },
      { type: "slider", label: "Slider" },
      { type: "rating", label: "Rating" },
    ],
  },
  {
    label: "Choice",
    types: [
      { type: "dropdown", label: "Dropdown" },
      { type: "checkbox", label: "Checkbox" },
      { type: "yes_no", label: "Yes / No" },
      { type: "emoji_scale", label: "Emoji Scale" },
    ],
  },
  {
    label: "Date & Time",
    types: [
      { type: "date", label: "Date" },
      { type: "time", label: "Time" },
      { type: "datetime", label: "Date & Time" },
    ],
  },
];

export function FieldTypePicker({ onSelect, disabled }: FieldTypePickerProps) {
  const [open, setOpen] = useState(false);

  function handleSelect(type: FieldType) {
    setOpen(false);
    onSelect(type);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full border-dashed"
          disabled={disabled}
        >
          {disabled ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add Field
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="space-y-3">
          {categories.map((cat) => (
            <div key={cat.label}>
              <p className="mb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {cat.label}
              </p>
              <div className="flex flex-wrap gap-1">
                {cat.types.map(({ type, label }) => (
                  <button
                    key={type}
                    onClick={() => handleSelect(type)}
                    className="rounded-md border px-2 py-1 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
